import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { buildRoute, ROUTES } from '@/config/routes'
import {
  ChevronDown, ChevronUp, Plus,
  CheckCircle2, Clock, AlertTriangle,
  FileText, ReceiptText, PenLine, Hammer,
  BadgeCheck, XCircle, Star, Download, Loader2,
} from 'lucide-react'
import { supabase } from '@/config/supabase'
import useAuthStore    from '@/stores/authStore'
import useBookingStore from '@/stores/bookingStore'
import Button from '@/components/ui/Button'

// ── Helpers ──────────────────────────────────────────────────────────────────

const fmt = (n) =>
  n != null && !isNaN(n)
    ? '\u20b1' + Number(n).toLocaleString('en-PH', { minimumFractionDigits: 0 })
    : null

function dateStr(iso) {
  if (!iso) return ''
  return new Date(iso).toLocaleDateString('en-PH', {
    month: 'short', day: 'numeric', year: 'numeric',
  })
}

function cityOf(address) {
  if (!address) return ''
  const parts = address.split(',')
  return parts.length > 1 ? parts[parts.length - 2]?.trim() : parts[0]?.trim()
}

// ── Stage derivation ─────────────────────────────────────────────────────────
// Returns { stage, label, color, icon, ctaKey }
// ctaKey drives what action buttons to show per card

function deriveStage(p) {
  const bStatus  = p.status
  const quote    = p.quote
  const req      = p.proposal_request
  const proposal = p.final_proposal
  const contract = p.contract
  const receipts = p.payments ?? []

  if (bStatus === 'cancelled') {
    return { stage: 'cancelled', label: 'Cancelled', color: 'text-siglat-red bg-siglat-red/10 border-siglat-red/25', icon: XCircle, ctaKey: 'none' }
  }
  if (bStatus === 'completed') {
    return { stage: 'completed', label: 'Completed', color: 'text-green-400 bg-green-500/10 border-green-500/25', icon: BadgeCheck, ctaKey: 'completed' }
  }
  if (bStatus === 'pending_approval') {
    return { stage: 'pending_approval', label: 'Work Done — Awaiting Verification', color: 'text-pink-400 bg-pink-500/10 border-pink-500/25', icon: CheckCircle2, ctaKey: 'waiting' }
  }
  if (['in_progress', 'assigned', 'en_route'].includes(bStatus)) {
    return { stage: 'active', label: 'Work In Progress', color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25', icon: Hammer, ctaKey: 'in_progress' }
  }
  if (bStatus === 'confirmed') {
    return { stage: 'confirmed', label: 'Confirmed', color: 'text-blue-400 bg-blue-500/10 border-blue-500/25', icon: CheckCircle2, ctaKey: 'in_progress' }
  }

  // Contract stage
  if (contract?.status === 'pending_customer') {
    return { stage: 'sign_contract', label: 'Sign Contract', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: PenLine, ctaKey: 'sign_contract' }
  }
  if (contract?.status === 'pending_sk') {
    return { stage: 'awaiting_sk_sign', label: 'Awaiting SK Signature', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: Clock, ctaKey: 'waiting' }
  }

  // Payment stage
  if (contract?.status === 'fully_signed' || proposal?.status === 'accepted') {
    const downpayment = receipts.find((r) => r.receipt_type === 'downpayment')
    const balance     = receipts.find((r) => r.receipt_type === 'balance')
    const fullPay     = receipts.find((r) => r.receipt_type === 'full')

    if (fullPay?.status === 'verified') {
      return { stage: 'payment_verified', label: 'Payment Verified', color: 'text-green-400 bg-green-500/10 border-green-500/25', icon: CheckCircle2, ctaKey: 'in_progress' }
    }
    if (fullPay?.status === 'pending') {
      return { stage: 'payment_verifying', label: 'Verifying Payment', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: Clock, ctaKey: 'waiting' }
    }
    if (downpayment?.status === 'verified' && !balance) {
      return { stage: 'balance_pending', label: 'Downpayment Received', color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25', icon: ReceiptText, ctaKey: 'upload_balance' }
    }
    if (downpayment?.status === 'pending') {
      return { stage: 'payment_verifying', label: 'Verifying Downpayment', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: Clock, ctaKey: 'waiting' }
    }
    if (balance?.status === 'pending') {
      return { stage: 'balance_verifying', label: 'Verifying Balance', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: Clock, ctaKey: 'waiting' }
    }
    return { stage: 'awaiting_payment', label: 'Awaiting Payment', color: 'text-yellow-400 bg-yellow-500/10 border-yellow-500/25', icon: ReceiptText, ctaKey: 'upload_payment' }
  }

  // Final proposal stage
  if (proposal?.status === 'sent' || proposal?.status === 'revision_requested') {
    return { stage: 'proposal_ready', label: 'Final Proposal Ready', color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25', icon: FileText, ctaKey: 'proposal_ready' }
  }
  if (proposal?.status === 'extended') {
    return { stage: 'proposal_extended', label: 'Proposal — Extension Granted', color: 'text-blue-400 bg-blue-500/10 border-blue-500/25', icon: Clock, ctaKey: 'proposal_ready' }
  }
  if (proposal?.status === 'declined') {
    return { stage: 'declined', label: 'Proposal Declined', color: 'text-siglat-red bg-siglat-red/10 border-siglat-red/25', icon: XCircle, ctaKey: 'none' }
  }

  // Proposal request stage
  if (req) {
    const reqLabels = {
      pending:              { label: 'Proposal Requested', ctaKey: 'waiting' },
      site_visit_required:  { label: 'Site Visit Required', ctaKey: 'site_visit' },
      site_visit_scheduled: { label: 'Visit Scheduled', ctaKey: 'waiting' },
      in_progress:          { label: 'Proposal In Progress', ctaKey: 'waiting' },
      uploaded:             { label: 'Proposal Uploaded', ctaKey: 'waiting' },
    }
    const r = reqLabels[req.status] ?? { label: 'Proposal Requested', ctaKey: 'waiting' }
    return { stage: 'proposal_requested', label: r.label, color: 'text-blue-400 bg-blue-500/10 border-blue-500/25', icon: Clock, ctaKey: r.ctaKey }
  }

  // Quote stage
  if (quote?.status === 'active') {
    return { stage: 'quoted', label: 'Estimate Ready', color: 'text-siglat-teal bg-siglat-teal/10 border-siglat-teal/25', icon: FileText, ctaKey: 'request_proposal' }
  }
  if (quote?.status === 'proposal_requested') {
    return { stage: 'proposal_requested', label: 'Proposal Requested', color: 'text-blue-400 bg-blue-500/10 border-blue-500/25', icon: Clock, ctaKey: 'waiting' }
  }

  return { stage: 'pending', label: 'Pending', color: 'text-ink-muted bg-surface-overlay border-surface-border', icon: Clock, ctaKey: 'waiting' }
}

// ── Stage badge ───────────────────────────────────────────────────────────────
function StageBadge({ stage }) {
  const Icon = stage.icon
  return (
    <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border font-medium ${stage.color}`}>
      <Icon className="w-3 h-3" />
      {stage.label}
    </span>
  )
}

// ── Payment terms summary ─────────────────────────────────────────────────────
function PaymentSummary({ proposal, payments }) {
  if (!proposal) return null
  const dp  = payments?.find((r) => r.receipt_type === 'downpayment')
  const bal = payments?.find((r) => r.receipt_type === 'balance')
  const fp  = payments?.find((r) => r.receipt_type === 'full')

  const total = proposal.total_amount
  const pct   = proposal.downpayment_pct ?? 50
  const dpAmt  = total ? Math.round(total * pct / 100) : null
  const balAmt = total ? Math.round(total * (100 - pct) / 100) : null

  if (proposal.payment_type === 'full' && total) {
    return (
      <div className="text-xs text-ink-muted">
        <span className="text-ink-secondary">Full payment: </span>
        <span className="text-ink-primary font-medium">{fmt(total)}</span>
        {fp?.status === 'verified' && <span className="ml-1 text-green-400">\u2713 Verified</span>}
      </div>
    )
  }
  if (proposal.payment_type === 'downpayment') {
    return (
      <div className="flex flex-col gap-0.5 text-xs">
        <div className="text-ink-muted">
          <span className="text-ink-secondary">Downpayment: </span>
          <span className="text-ink-primary font-medium">{fmt(dpAmt)}</span>
          {dp?.status === 'verified' && <span className="ml-1 text-green-400">\u2713 Received</span>}
        </div>
        <div className="text-ink-muted">
          <span className="text-ink-secondary">Balance: </span>
          <span className="text-ink-primary font-medium">{fmt(balAmt)}</span>
          <span className="text-ink-muted ml-1">(due on completion)</span>
          {bal?.status === 'verified' && <span className="ml-1 text-green-400">\u2713 Received</span>}
        </div>
      </div>
    )
  }
  return null
}

// ── CTA buttons per stage ─────────────────────────────────────────────────────
function ProjectCTAs({ project, stage, onRequestProposal }) {
  const navigate = useNavigate()
  const [pdfSignedUrl, setPdfSignedUrl] = useState(null)
  const pdfPath = project.final_proposal?.pdf_path
  useEffect(() => {
    if (!pdfPath) { setPdfSignedUrl(null); return }
    supabase.storage.from('final-proposals')
      .createSignedUrl(pdfPath, 3600)
      .then(({ data }) => setPdfSignedUrl(data?.signedUrl ?? null))
  }, [pdfPath])

  if (stage.ctaKey === 'none') return null

  if (stage.ctaKey === 'waiting') {
    const msg = stage.stage === 'pending_approval'
      ? 'Your worker has submitted the completed job. Siglat Konstract is reviewing the work.'
      : 'Siglat Konstract is preparing your next step.'
    return <p className="text-xs text-ink-muted italic">{msg}</p>
  }

  if (stage.ctaKey === 'request_proposal') {
    return (
      <Button size="sm" onClick={() => onRequestProposal(project)}>
        Request Final Proposal
      </Button>
    )
  }

  if (stage.ctaKey === 'proposal_ready') {
    const proposalId = project.final_proposal?.id
    return (
      <div className="flex flex-wrap gap-2">
        {pdfSignedUrl && (
          <a href={pdfSignedUrl} target="_blank" rel="noopener noreferrer">
            <Button size="sm" variant="secondary" leftIcon={<FileText className="w-3.5 h-3.5" />}>
              View Proposal
            </Button>
          </a>
        )}
        <Button
          size="sm"
          leftIcon={<PenLine className="w-3.5 h-3.5" />}
          onClick={() => navigate(buildRoute.bookContract(proposalId))}
          disabled={!proposalId}
        >
          Proceed &amp; Sign Contract
        </Button>
      </div>
    )
  }

  if (stage.ctaKey === 'sign_contract') {
    const proposalId = project.final_proposal?.id
    return (
      <Button
        size="sm"
        leftIcon={<PenLine className="w-3.5 h-3.5" />}
        onClick={() => navigate(buildRoute.bookContract(proposalId))}
        disabled={!proposalId}
      >
        Sign Contract
      </Button>
    )
  }

  if (stage.ctaKey === 'upload_payment') {
    const proposalId = project.final_proposal?.id
    return (
      <Button
        size="sm"
        leftIcon={<ReceiptText className="w-3.5 h-3.5" />}
        onClick={() => navigate(buildRoute.bookPayment(proposalId))}
        disabled={!proposalId}
      >
        Upload Payment Receipt
      </Button>
    )
  }

  if (stage.ctaKey === 'upload_balance') {
    const proposalId = project.final_proposal?.id
    return (
      <Button
        size="sm"
        variant="secondary"
        leftIcon={<ReceiptText className="w-3.5 h-3.5" />}
        onClick={() => navigate(buildRoute.bookPayment(proposalId))}
        disabled={!proposalId}
      >
        Upload Balance Receipt
      </Button>
    )
  }

  if (stage.ctaKey === 'site_visit') {
    const reqId = project.proposal_request?.id
    return (
      <Button
        size="sm"
        leftIcon={<Clock className="w-3.5 h-3.5" />}
        onClick={() => navigate(buildRoute.bookSiteVisit(reqId))}
        disabled={!reqId}
      >
        Schedule Site Visit
      </Button>
    )
  }

  if (stage.ctaKey === 'completed') {
    const bookingId = project.id
    const hasWarranty = !!project.warranty_expires
    return (
      <div className="flex flex-wrap gap-2">
        {!hasWarranty && (
          <Button
            size="sm"
            leftIcon={<BadgeCheck className="w-3.5 h-3.5" />}
            onClick={() => navigate(buildRoute.bookComplete(bookingId))}
          >
            Accept &amp; Activate Warranty
          </Button>
        )}
        <Button
          size="sm"
          variant="secondary"
          leftIcon={<Star className="w-3.5 h-3.5" />}
          onClick={() => navigate(buildRoute.bookReview(bookingId))}
        >
          Rate Worker
        </Button>
      </div>
    )
  }

  return null
}

// ── Contract PDF download ─────────────────────────────────────────────────────
function ContractDownloadButton({ pdfPath }) {
  const [url,     setUrl]     = useState(null)
  const [loading, setLoading] = useState(false)

  async function handleDownload() {
    if (url) { window.open(url, '_blank'); return }
    setLoading(true)
    const { data } = await supabase.storage.from('contracts').createSignedUrl(pdfPath, 3600)
    if (data?.signedUrl) {
      setUrl(data.signedUrl)
      window.open(data.signedUrl, '_blank')
    }
    setLoading(false)
  }

  return (
    <button
      onClick={handleDownload}
      disabled={loading}
      className="inline-flex items-center gap-2 text-xs px-3 py-2 rounded-lg bg-surface-overlay border border-surface-border text-ink-secondary hover:border-siglat-teal/40 hover:text-siglat-teal transition-colors disabled:opacity-50"
    >
      {loading
        ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
        : <Download className="w-3.5 h-3.5" />}
      Download Contract PDF
    </button>
  )
}

// ── Single project card ───────────────────────────────────────────────────────
function ProjectCard({ project, onRequestProposal }) {
  const [open, setOpen] = useState(false)
  const stage = deriveStage(project)

  const city    = cityOf(project.address)
  const created = dateStr(project.created_at)
  const q       = project.quote

  return (
    <div className={`bg-surface-raised border rounded-xl overflow-hidden transition-colors
      ${open ? 'border-siglat-teal/40' : 'border-surface-border'}`}>

      {/* Header row — always visible */}
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-4 py-4 flex items-center justify-between gap-3 text-left"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1.5">
            <p className="text-sm font-semibold text-ink-primary">
              {q?.quote_reference ?? project.ref_number}
            </p>
            <StageBadge stage={stage} />
          </div>
          <p className="text-sm text-ink-primary leading-snug">
            {project.category_name}
          </p>
          <p className="text-xs text-ink-muted mt-0.5">
            {city}{city && created ? ' \u00b7 ' : ''}{created}
          </p>
        </div>
        <span className="text-ink-muted shrink-0">
          {open
            ? <ChevronUp className="w-4 h-4" />
            : <ChevronDown className="w-4 h-4" />
          }
        </span>
      </button>

      {/* Expanded detail */}
      {open && (
        <div className="border-t border-surface-border px-4 py-4 flex flex-col gap-4 animate-fade-in">

          {/* Address */}
          {project.address && (
            <div>
              <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-1">Address</p>
              <p className="text-sm text-ink-primary">{project.address}</p>
            </div>
          )}

          {/* Estimate range */}
          {q && (q.total_min || q.total_max) && (
            <div>
              <p className="text-[10px] text-ink-muted uppercase tracking-widest mb-1">System Estimate</p>
              <p className="text-sm font-semibold text-siglat-teal">
                {fmt(q.total_min)} &ndash; {fmt(q.total_max)}
              </p>
              <p className="text-xs text-ink-muted">May vary based on site conditions</p>
            </div>
          )}

          {/* Payment terms (once proposal exists) */}
          <PaymentSummary
            proposal={project.final_proposal}
            payments={project.payments}
          />

          {/* Proposal request status note */}
          {project.proposal_request && (
            <div className="bg-surface-overlay border border-surface-border rounded-lg px-3 py-2.5">
              <p className="text-xs text-ink-muted">
                Final Proposal Request &mdash;{' '}
                <span className="text-ink-primary font-medium capitalize">
                  {project.proposal_request.status?.replace(/_/g, ' ')}
                </span>
              </p>
              {project.proposal_request.preferred_visit_date && (
                <p className="text-xs text-ink-muted mt-0.5">
                  Preferred visit: {dateStr(project.proposal_request.preferred_visit_date + 'T00:00:00')}
                </p>
              )}
            </div>
          )}

          {/* Contract PDF (available once fully signed) */}
          {project.contract?.pdf_path && (
            <ContractDownloadButton pdfPath={project.contract.pdf_path} />
          )}

          {/* CTAs */}
          <ProjectCTAs
            project={project}
            stage={stage}
            onRequestProposal={onRequestProposal}
          />

        </div>
      )}
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────
export default function MyProjects() {
  const navigate = useNavigate()
  const profile  = useAuthStore((s) => s.profile)

  const [projects, setProjects] = useState([])
  const [loading,  setLoading]  = useState(true)
  const [error,    setError]    = useState(null)

  useEffect(() => {
    if (!profile?.id) return
    fetchProjects()
  }, [profile?.id]) // eslint-disable-line

  async function fetchProjects() {
    setLoading(true)
    setError(null)

    // Step 1: fetch bookings with only service_categories (no quotes embed — avoids
    // ambiguous FK: bookings.quote_id → quotes AND quotes.booking_id → bookings)
    const { data: bookings, error: err } = await supabase
      .from('bookings')
      .select(`
        id,
        ref_number,
        status,
        address,
        created_at,
        warranty_expires,
        service_categories ( name )
      `)
      .eq('customer_id', profile.id)
      .not('status', 'eq', 'cancelled')
      .order('created_at', { ascending: false })

    if (err) {
      setError('Could not load your projects. Please try again.')
      setLoading(false)
      return
    }

    const bookingIds = (bookings ?? []).map((b) => b.id).filter(Boolean)

    // Step 2: batch fetch quotes + proposals in parallel
    const [
      { data: quotesRaw },
      { data: proposals },
    ] = await Promise.all([
      bookingIds.length > 0
        ? supabase
            .from('quotes')
            .select('id, booking_id, quote_reference, status, total_min, total_max')
            .in('booking_id', bookingIds)
        : Promise.resolve({ data: [] }),
      bookingIds.length > 0
        ? supabase
            .from('final_proposals')
            .select('id, booking_id, status, payment_type, total_amount, downpayment_pct, pdf_path')
            .in('booking_id', bookingIds)
        : Promise.resolve({ data: [] }),
    ])

    const quoteIds    = (quotesRaw ?? []).map((q) => q.id).filter(Boolean)
    const proposalIds = (proposals  ?? []).map((p) => p.id).filter(Boolean)

    // Step 3: fetch fprs + contracts in parallel
    // contracts.booking_id is always null — must join via proposal_id
    let fprs = [], contracts = []
    await Promise.all([
      quoteIds.length > 0
        ? supabase
            .from('final_proposal_requests')
            .select('id, quote_id, status, preferred_visit_date')
            .in('quote_id', quoteIds)
            .then(({ data }) => { fprs = data ?? [] })
        : Promise.resolve(),
      proposalIds.length > 0
        ? supabase
            .from('contracts')
            .select('id, booking_id, proposal_id, status, customer_signed_at, sk_signed_at, pdf_path')
            .in('proposal_id', proposalIds)
            .then(({ data }) => { contracts = data ?? [] })
        : Promise.resolve(),
    ])

    // Step 4: fetch payment_receipts for found proposals
    let receipts = []
    if (proposalIds.length > 0) {
      const { data: r } = await supabase
        .from('payment_receipts')
        .select('id, proposal_id, receipt_type, status, amount_stated')
        .in('proposal_id', proposalIds)
      receipts = r ?? []
    }

    // Step 5: build lookup maps and shape final data
    const quoteMap = {}
    for (const q of quotesRaw ?? []) quoteMap[q.booking_id] = q

    const fprMap = {}
    for (const f of fprs) fprMap[f.quote_id] = f

    const proposalMap = {}
    for (const p of proposals ?? []) proposalMap[p.booking_id] = p

    const contractMap = {}
    for (const c of contracts) contractMap[c.proposal_id] = c

    const shaped = (bookings ?? []).map((b) => {
      const quote = quoteMap[b.id] ?? null
      const req   = quote ? (fprMap[quote.id] ?? null) : null
      const fp    = proposalMap[b.id] ?? null

      return {
        id:               b.id,
        ref_number:       b.ref_number,
        status:           b.status,
        address:          b.address,
        created_at:       b.created_at,
        warranty_expires: b.warranty_expires,
        category_name:    b.service_categories?.name ?? 'Service',
        quote:            quote
          ? {
              id:              quote.id,
              quote_reference: quote.quote_reference,
              status:          quote.status,
              total_min:       quote.total_min,
              total_max:       quote.total_max,
            }
          : null,
        proposal_request: req,
        final_proposal:   fp,
        contract:         fp ? (contractMap[fp.id] ?? null) : null,
        payments:         fp ? receipts.filter((r) => r.proposal_id === fp.id) : [],
      }
    })

    setProjects(shaped)
    setLoading(false)
  }

  function handleRequestProposal(project) {
    useBookingStore.getState().setBookingId(project.id, project.ref_number)
    if (project.quote?.id) useBookingStore.getState().setQuoteId(project.quote.id)
    navigate(ROUTES.BOOK_FINAL_PROPOSAL)
  }

  // ── Active vs history split ───────────────────────────────────────────────
  const cutoff  = new Date()
  cutoff.setDate(cutoff.getDate() - 90)

  const active  = projects.filter(
    (p) => p.status !== 'completed' ||
           new Date(p.created_at) > cutoff
  )
  const history = projects.filter(
    (p) => p.status === 'completed' &&
           new Date(p.created_at) <= cutoff
  )

  return (
    <div className="min-h-dvh bg-surface-base flex flex-col">

      {/* Header */}
      <div className="bg-surface-raised border-b border-surface-border px-4 pt-12 pb-5 safe-top shrink-0">
        <div className="page-container">
          <h1 className="font-display text-2xl font-bold text-ink-primary">My Projects</h1>
          <p className="text-sm text-ink-muted mt-0.5">
            {profile?.full_name?.split(' ')[0] ?? ''}&rsquo;s active bookings with Siglat Konstract
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto pb-28">
        <div className="page-container py-5 flex flex-col gap-3">

          {loading ? (
            <div className="flex flex-col gap-2">
              {[1,2,3].map((n) => (
                <div key={n} className="h-24 bg-surface-raised rounded-xl animate-pulse border border-surface-border" />
              ))}
            </div>
          ) : error ? (
            <div className="flex flex-col items-center gap-3 py-12 text-center">
              <AlertTriangle className="w-8 h-8 text-siglat-red" />
              <p className="text-sm text-ink-primary">{error}</p>
              <button onClick={fetchProjects} className="text-sm text-siglat-teal hover:underline">
                Try again
              </button>
            </div>
          ) : active.length === 0 && history.length === 0 ? (
            <div className="flex flex-col items-center gap-4 py-16 text-center px-6">
              <div className="w-14 h-14 bg-surface-raised rounded-2xl border border-surface-border flex items-center justify-center">
                <Hammer className="w-6 h-6 text-ink-muted" />
              </div>
              <div>
                <p className="font-semibold text-ink-primary">No projects yet</p>
                <p className="text-sm text-ink-muted mt-1">
                  Book your first service to get started.
                </p>
              </div>
            </div>
          ) : (
            <>
              {/* Active projects */}
              {active.map((p) => (
                <ProjectCard
                  key={p.id}
                  project={p}
                  onRequestProposal={handleRequestProposal}
                />
              ))}

              {/* History (90+ days completed) */}
              {history.length > 0 && (
                <>
                  <p className="text-xs text-ink-muted uppercase tracking-widest mt-3 px-1">
                    History
                  </p>
                  {history.map((p) => (
                    <ProjectCard
                      key={p.id}
                      project={p}
                      onRequestProposal={handleRequestProposal}
                    />
                  ))}
                </>
              )}
            </>
          )}

        </div>
      </div>

      {/* Sticky bottom CTA */}
      <div className="fixed bottom-0 inset-x-0 bg-surface-raised border-t border-surface-border px-4 py-4 safe-bottom z-10">
        <div className="page-container">
          <Button
            fullWidth
            size="lg"
            leftIcon={<Plus className="w-4 h-4" />}
            onClick={() => {
              useBookingStore.getState().reset()
              navigate(ROUTES.BOOK)
            }}
          >
            Book New Service
          </Button>
        </div>
      </div>

    </div>
  )
}
